import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";
import { formatPrice } from "../../utils/currency";
import "./SuperAdminDashboard.css";

function getToday() {
  return new Date().toISOString().slice(0, 10);
}
function getMonthStart() {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}
function getMonthEnd() {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  return d.toISOString().slice(0, 10);
}
function formatDateTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function SuperAdminDashboard() {
  const { api, logout, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [venues, setVenues] = useState([]);
  const [venueSummaries, setVenueSummaries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateFrom, setDateFrom] = useState(getMonthStart());
  const [dateTo, setDateTo] = useState(getMonthEnd());
  const [statusFilter, setStatusFilter] = useState("");
  const [venueFilter, setVenueFilter] = useState("");
  const [showCreateVenue, setShowCreateVenue] = useState(false);
  const [newVenue, setNewVenue] = useState({ name: "", slug: "", address: "" });

  const fetchVenues = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api(`query { venues { venue_id name slug address is_active } }`);
      const data = res.data?.data;
      setVenues(data?.venues || []);
    } catch (err) {
      setError(err.message || "Failed to load venues");
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchVenueSummaries = useCallback(async () => {
    const today = getToday();
    const monthStart = getMonthStart();
    const monthEnd = getMonthEnd();
    const venueIds = venues.map((v) => v.venue_id);
    const summaries = [];
    for (const vid of venueIds) {
      try {
        const res1 = await api(
          `query($vid: Int, $today: String) {
            salesSummary(venue_id: $vid, date_from: $today, date_to: $today) { total_orders total_revenue }
          }`,
          { vid, today }
        );
        const todayS = res1.data?.data?.salesSummary || {};
        const res2 = await api(
          `query($vid: Int, $ms: String, $me: String) {
            salesSummary(venue_id: $vid, date_from: $ms, date_to: $me) { total_orders total_revenue }
          }`,
          { vid, ms: monthStart, me: monthEnd }
        );
        const monthS = res2.data?.data?.salesSummary || {};
        const venue = venues.find((v) => v.venue_id === vid);
        summaries.push({
          venue_id: vid,
          name: venue?.name || `Venue ${vid}`,
          today_orders: todayS.total_orders || 0,
          today_revenue: todayS.total_revenue || 0,
          month_orders: monthS.total_orders || 0,
          month_revenue: monthS.total_revenue || 0,
        });
      } catch (e) {
        summaries.push({
          venue_id: vid,
          name: venues.find((v) => v.venue_id === vid)?.name || `Venue ${vid}`,
          today_orders: 0,
          today_revenue: 0,
          month_orders: 0,
          month_revenue: 0,
        });
      }
    }
    setVenueSummaries(summaries);
  }, [api, venues]);

  const fetchOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const vid = venueFilter ? parseInt(venueFilter, 10) : undefined;
      const res = await api(
        `query($status: String, $date_from: String, $date_to: String, $venue_id: Int, $limit: Int, $offset: Int) {
          orders(status: $status, date_from: $date_from, date_to: $date_to, venue_id: $venue_id, limit: $limit, offset: $offset) {
            order_id order_number customer_token customer_name total_amount order_status kiosk_type venue_id created_at
          }
        }`,
        {
          status: statusFilter || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          venue_id: vid,
          limit: 100,
          offset: 0,
        }
      );
      setOrders(res.data?.data?.orders || []);
    } catch (err) {
      setOrders([]);
      setError(err.message);
    } finally {
      setOrdersLoading(false);
    }
  }, [api, dateFrom, dateTo, statusFilter, venueFilter]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    fetchVenues();
  }, [isSuperAdmin, fetchVenues]);

  useEffect(() => {
    if (venues.length === 0) return;
    fetchVenueSummaries();
  }, [venues, fetchVenueSummaries]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleCreateVenue = async (e) => {
    e.preventDefault();
    if (!newVenue.name.trim()) {
      toast.error("Name required");
      return;
    }
    try {
      await api(
        `mutation($input: CreateVenueInput!) { createVenue(input: $input) { venue_id name slug } }`,
        {
          input: {
            name: newVenue.name.trim(),
            slug: (newVenue.slug || newVenue.name.toLowerCase().replace(/\s+/g, "-")).replace(/[^a-z0-9-]/g, ""),
            address: newVenue.address.trim() || null,
          },
        }
      );
      toast.success("Venue created");
      setShowCreateVenue(false);
      setNewVenue({ name: "", slug: "", address: "" });
      fetchVenues();
    } catch (err) {
      toast.error(err.message || "Failed to create venue");
    }
  };

  const handleToggleVenue = async (venue) => {
    try {
      await api(
        `mutation($venue_id: Int!, $input: UpdateVenueInput!) { updateVenue(venue_id: $venue_id, input: $input) { venue_id is_active } }`,
        { venue_id: venue.venue_id, input: { is_active: !venue.is_active } }
      );
      toast.success(venue.is_active ? "Venue deactivated" : "Venue activated");
      fetchVenues();
    } catch (err) {
      toast.error(err.message || "Failed to update venue");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  if (!isSuperAdmin) {
    return (
      <div className="admin-dashboard">
        <p>Unauthorized. Redirecting...</p>
        {setTimeout(() => navigate("/admin/login"), 1000)}
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <header className="admin-dashboard-header">
        <h1>Super Admin</h1>
        <p>Manage venues and view analytics</p>
        <button onClick={handleLogout} className="btn-logout">
          Logout
        </button>
      </header>

      {error && <div className="admin-error">{error}</div>}

      <section className="admin-section">
        <h2>Venues</h2>
        <button onClick={() => setShowCreateVenue(true)} className="btn-primary">
          Add Venue
        </button>
        {showCreateVenue && (
          <form onSubmit={handleCreateVenue} className="admin-form">
            <input
              placeholder="Name"
              value={newVenue.name}
              onChange={(e) => setNewVenue((v) => ({ ...v, name: e.target.value }))}
            />
            <input
              placeholder="Slug (optional)"
              value={newVenue.slug}
              onChange={(e) => setNewVenue((v) => ({ ...v, slug: e.target.value }))}
            />
            <input
              placeholder="Address (optional)"
              value={newVenue.address}
              onChange={(e) => setNewVenue((v) => ({ ...v, address: e.target.value }))}
            />
            <button type="submit">Create</button>
            <button type="button" onClick={() => setShowCreateVenue(false)}>
              Cancel
            </button>
          </form>
        )}
        <div className="venue-list">
          {venues.map((v) => (
            <div key={v.venue_id} className="venue-card">
              <div>
                <strong>{v.name}</strong> ({v.slug})
                {!v.is_active && <span className="badge-inactive">Inactive</span>}
              </div>
              <button onClick={() => handleToggleVenue(v)} className="btn-sm">
                {v.is_active ? "Deactivate" : "Activate"}
              </button>
            </div>
          ))}
        </div>
      </section>

      <section className="admin-section">
        <h2>Venue Analytics</h2>
        {loading ? (
          <p>Loading...</p>
        ) : (
          <div className="kpi-grid">
            {venueSummaries.map((s) => (
              <div key={s.venue_id} className="kpi-card">
                <h3>{s.name}</h3>
                <div>Today: {s.today_orders} orders, {formatPrice(s.today_revenue)}</div>
                <div>Month: {s.month_orders} orders, {formatPrice(s.month_revenue)}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="admin-section">
        <h2>Orders</h2>
        <div className="filters">
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          <select value={venueFilter} onChange={(e) => setVenueFilter(e.target.value)}>
            <option value="">All Venues</option>
            {venues.map((v) => (
              <option key={v.venue_id} value={v.venue_id}>{v.name}</option>
            ))}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="cooking">Cooking</option>
            <option value="ready">Ready</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button onClick={fetchOrders}>Apply</button>
        </div>
        <div className="orders-table-wrap">
          {ordersLoading ? (
            <div>Loading orders...</div>
          ) : (
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Order #</th>
                  <th>Venue</th>
                  <th>Customer</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.order_id}>
                    <td>{formatDateTime(o.created_at)}</td>
                    <td>{o.order_number || o.order_id}</td>
                    <td>{venues.find((v) => v.venue_id === o.venue_id)?.name || o.venue_id || "—"}</td>
                    <td>{o.customer_name || "—"}</td>
                    <td>{formatPrice(o.total_amount || 0)}</td>
                    <td><span className={`status-badge status-${o.order_status || "pending"}`}>{o.order_status || "pending"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!ordersLoading && orders.length === 0 && <div>No orders in this range.</div>}
        </div>
      </section>
    </div>
  );
}

export default SuperAdminDashboard;
